// Maps report content from ReportLow/Mid/High pages for PDF generation

export interface ReportContent {
  header: {
    title: string;
    description: string;
  };
  stats: Array<{
    label: string;
    value: string;
  }>;
  recommendations: Array<{
    title: string;
    description: string;
    steps: string[];
  }>;
  advancedTips?: Array<{
    title: string;
    description: string;
  }>;
  commonMistakes?: Array<{
    title: string;
    description: string;
  }>;
  competitiveAdvantages?: Array<{
    title: string;
    description: string;
  }>;
  quickWins: Array<{
    title: string;
    description: string;
    timeline: string;
    impact: string;
  }>;
}

export function getReportContent(level: 'low' | 'mid' | 'high'): ReportContent {
  if (level === 'low') {
    return {
      header: {
        title: "Sleeper - Podstawowa strategia LinkedIn",
        description: "Twoja obecność na LinkedIn jest minimalna lub nieaktywna. To doskonały moment, aby zacząć budować swoją profesjonalną markę. Poniżej znajdziesz konkretne kroki do rozpoczęcia."
      },
      stats: [
        { label: "Czas wdrożenia", value: "1-2 miesiące" },
        { label: "Wymagany budżet", value: "Minimalny" },
        { label: "Potencjał wzrostu", value: "Bardzo wysoki" },
        { label: "Priorytet", value: "Budowa fundamentów" }
      ],
      recommendations: [
        {
          title: "Zoptymalizuj profil LinkedIn",
          description: "Profesjonalny profil to fundament Twojej obecności na LinkedIn. Poświęć czas na dopracowanie każdej sekcji.",
          steps: [
            "Dodaj profesjonalne zdjęcie profilowe (twarz zajmuje 60% kadru, neutralne tło)",
            "Stwórz przekonujący nagłówek (headline) zawierający Twoją rolę i wartość",
            "Napisz sekcję 'O mnie' (About) w pierwszej osobie, podkreślając osiągnięcia",
            "Wypełnij sekcję doświadczenia zawodowego z opisem wyników (nie tylko obowiązków)",
            "Dodaj relewantne umiejętności i poproś o endorsements"
          ]
        },
        {
          title: "Zbuduj strategię treści",
          description: "Regularne publikowanie wartościowych treści to klucz do budowania widoczności i autorytetu.",
          steps: [
            "Zaplanuj kalendarz publikacji (minimum 2-3 posty tygodniowo)",
            "Wybierz 3-5 głównych tematów związanych z Twoją branżą",
            "Przygotuj listę pomysłów na posty (case studies, porady, doświadczenia)",
            "Określ optymalny czas publikacji (testuj różne godziny)",
            "Śledź zaangażowanie i dostosuj strategię"
          ]
        },
        {
          title: "Rozbuduj sieć kontaktów",
          description: "Aktywnie buduj sieć profesjonalnych kontaktów w swojej branży.",
          steps: [
            "Dodaj minimum 50 nowych kontaktów miesięcznie (z przemyślanym zaproszeniem)",
            "Priorytetyzuj kontakty: klienci, partnerzy, liderzy branżowi",
            "Angażuj się w posty swoich kontaktów (komentarze, reakcje)",
            "Dołącz do 5-10 grup LinkedIn związanych z Twoją branżą",
            "Regularnie przeglądaj i aktualizuj listę kontaktów"
          ]
        }
      ],
      commonMistakes: [
        {
          title: "Brak regularności w publikacji",
          description: "Sporadyczne posty nie budują zaangażowania ani widoczności. Kluczem jest konsystencja - lepiej 2 posty tygodniowo przez rok, niż 10 postów w miesiącu, a potem cisza."
        },
        {
          title: "Ignorowanie komentarzy",
          description: "LinkedIn nagradza algorytmicznie zaangażowanie. Odpowiadaj na każdy komentarz w ciągu pierwszych 2 godzin od publikacji - to zwiększa zasięg posta."
        },
        {
          title: "Autopromocja bez wartości",
          description: "Unikaj postów typu 'Kupi ktoś?'. Zamiast tego dziel się wiedzą, case studies, lekcjami z niepowodzeń. Wartość przyciąga uwagę lepiej niż reklama."
        },
        {
          title: "Kopiowanie cudzych sukcesów",
          description: "Autentyczność wygrywa. Nie kopiuj stylu innych - znajdź własny głos. Ludzie łączą się z osobami, nie z maskami."
        }
      ],
      quickWins: [
        {
          title: "Aktualizacja zdjęcia profilowego",
          description: "Dodaj profesjonalne zdjęcie zgodne z wytycznymi LinkedIn",
          timeline: "Dzisiaj",
          impact: "Wysoki - zwiększa zaufanie o 14x"
        },
        {
          title: "Optymalizacja nagłówka",
          description: "Przepisz headline, aby pokazywał wartość, nie tylko stanowisko",
          timeline: "Dzisiaj",
          impact: "Wysoki - to pierwsze, co widzą odwiedzający"
        },
        {
          title: "Pierwsze 3 posty",
          description: "Opublikuj 3 wartościowe posty w ciągu najbliższego tygodnia",
          timeline: "Tydzień",
          impact: "Średni - buduje momentum"
        },
        {
          title: "50 nowych kontaktów",
          description: "Wyślij przemyślane zaproszenia do 50 osób z branży",
          timeline: "2 tygodnie",
          impact: "Średni - rozbudowuje zasięg"
        }
      ]
    };
  }

  if (level === 'mid') {
    return {
      header: {
        title: "Challenger - Średniozaawansowana strategia LinkedIn",
        description: "Masz solidne podstawy, ale jest miejsce na optymalizację i skalowanie działań. Czas przejść z aktywności na strategię, która przynosi mierzalne rezultaty biznesowe."
      },
      stats: [
        { label: "Czas wdrożenia", value: "Kilka tygodni" },
        { label: "Wymagany budżet", value: "Średni" },
        { label: "Potencjał wzrostu", value: "Wysoki" },
        { label: "Priorytet", value: "Skalowanie działań" }
      ],
      recommendations: [
        {
          title: "Optymalizuj strategię treści",
          description: "Przejdź od sporadycznych postów do przemyślanej strategii content marketingu opartej na danych.",
          steps: [
            "Analizuj wyniki poprzednich postów (zaangażowanie, zasięg, konwersje)",
            "Określ content pillars - 3-4 główne tematy, które rezonują z odbiorcami",
            "Testuj różne formaty: karuzele, video, long-form, ankiety",
            "Wdróż proces batch creation - przygotowuj treści z tygodniowym wyprzedzeniem",
            "Wykorzystaj LinkedIn Analytics do optymalizacji czasów publikacji"
          ]
        },
        {
          title: "Zbuduj zaangażowaną społeczność",
          description: "Skup się na jakości interakcji, nie tylko na liczbie followersów.",
          steps: [
            "Zidentyfikuj 20-30 kluczowych profili w Twojej niszy i regularnie z nimi wchodź w interakcje",
            "Odpowiadaj na każdy komentarz w ciągu 2h od publikacji",
            "Inicjuj dyskusje w swoich postach poprzez pytania",
            "Stwórz 'engagement pod' - grupę 5-10 osób, które wzajemnie się wspierają",
            "Organizuj LinkedIn Live lub audio eventy raz w miesiącu"
          ]
        },
        {
          title: "Wdróż lead generation",
          description: "Przekształć widoczność w konkretne leady biznesowe.",
          steps: [
            "Stwórz lead magnet (ebook, checklist, template) i promuj w postach",
            "Wykorzystaj LinkedIn Newsletter do budowania bazy subskrybentów",
            "Wdróż prosty CRM do śledzenia interakcji z potencjalnymi klientami",
            "Testuj różne call-to-action w postach (komentarz, DM, link)",
            "Mierz konwersję z LinkedIn na konsultacje/sprzedaż"
          ]
        },
        {
          title: "Zaangażuj zespół",
          description: "Zwiększ zasięg poprzez aktywację pracowników jako ambasadorów marki.",
          steps: [
            "Zidentyfikuj 3-5 kluczowych osób w zespole gotowych do aktywności",
            "Przeprowadź warsztaty z optymalizacji profili",
            "Stwórz zbiór ready-to-share content dla pracowników",
            "Wdróż prosty system zachęt (gamifikacja, uznanie)",
            "Śledź rezultaty employee advocacy w LinkedIn Analytics"
          ]
        }
      ],
      advancedTips: [
        {
          title: "Wykorzystaj LinkedIn Sales Navigator",
          description: "Jeśli Twój cel to B2B lead generation, Sales Navigator to najlepsze 70€/msc które możesz wydać. Pozwala na precyzyjne targetowanie, śledzenie leadów i zaawansowane wyszukiwanie."
        },
        {
          title: "Testuj LinkedIn Ads",
          description: "Zacznij od małych budżetów (500-1000 zł/msc). Testuj Sponsored Content dla zwiększenia zasięgu najlepszych postów organicznych. ROI może być świetny przy dobrym targetowaniu."
        },
        {
          title: "Wykorzystaj Creator Mode",
          description: "Włączenie Creator Mode zmienia układ profilu, dodaje sekcję Featured i zwiększa widoczność Twoich postów. Idealne jeśli Twoja strategia opiera się na content marketingu."
        },
        {
          title: "Kolaboracje z innymi twórcami",
          description: "Zaproś innych liderów z branży do wspólnych projektów: LinkedIn Live, collaborative articles, guest posts. Wzajemne promowanie zwiększa zasięg o 200-300%."
        }
      ],
      quickWins: [
        {
          title: "Content audit",
          description: "Przeanalizuj ostatnie 20 postów, zidentyfikuj top 3 tematy",
          timeline: "Dzisiaj",
          impact: "Wysoki - fokus na tym co działa"
        },
        {
          title: "Engagement pod",
          description: "Stwórz grupę 5 osób do wzajemnego wspierania postów",
          timeline: "Ten tydzień",
          impact: "Wysoki - natychmiastowy wzrost zasięgu"
        },
        {
          title: "Lead magnet",
          description: "Przygotuj prosty ebook/checklist do generowania leadów",
          timeline: "2 tygodnie",
          impact: "Bardzo wysoki - rozpoczyna lead generation"
        },
        {
          title: "Creator Mode",
          description: "Włącz Creator Mode i wypełnij sekcję Featured",
          timeline: "Dzisiaj",
          impact: "Średni - lepszy układ profilu"
        }
      ]
    };
  }

  // level === 'high'
  return {
    header: {
      title: "Dominacja - Zaawansowana strategia LinkedIn",
      description: "Gratulacje! Twoja strategia LinkedIn jest na wysokim poziomie. Teraz czas na skalowanie, automatyzację i maksymalizację ROI. Poniżej znajdziesz taktyki stosowane przez top 1% LinkedIn creators."
    },
    stats: [
      { label: "Czas optymalizacji", value: "Ciągły proces" },
      { label: "Wymagany budżet", value: "Średni-wysoki" },
      { label: "Potencjał wzrostu", value: "Średni-wysoki" },
      { label: "Priorytet", value: "Maksymalizacja ROI" }
    ],
    recommendations: [
      {
        title: "Zaawansowana strategia treści",
        description: "Wykorzystaj dane do tworzenia viralnych treści i budowania thought leadership.",
        steps: [
          "Wdróż zaawansowaną analitykę (UTM tracking, LinkedIn Insight Tag)",
          "Testuj storytelling formats: case studies, behind-the-scenes, controversial takes",
          "Wykorzystaj AI do optymalizacji headlines i hook sentences",
          "Stwórz content framework (formula na powtarzalne sukcesy)",
          "Mierz earned media value (EMV) Twojej obecności na LinkedIn"
        ]
      },
      {
        title: "Skaluj zasięg poprzez LinkedIn Ads",
        description: "Wykorzystaj płatne reklamy do przyspieszenia wzrostu i generowania leadów.",
        steps: [
          "Promuj najlepsze organiczne posty poprzez Sponsored Content",
          "Testuj Conversation Ads do bezpośredniego lead generation",
          "Wykorzystaj Matched Audiences do retargetingu website visitors",
          "Optymalizuj kampanie pod kątem cost-per-lead, nie impressions",
          "A/B testuj kreacje, copy, targeting - minimalny budżet 3000 zł/msc"
        ]
      },
      {
        title: "Zbuduj program Employee Advocacy",
        description: "Przekształć zespół w armię brand ambasadorów.",
        steps: [
          "Wdróż platformę do employee advocacy (np. Oktopost, PostBeyond)",
          "Stwórz szkolenia LinkedIn dla całego zespołu",
          "Przygotuj content library z ready-to-share postami",
          "Gamifikuj program (leaderboard, nagrody)",
          "Mierz zespołowy Social Selling Index i indywidualny wkład"
        ]
      },
      {
        title: "Zaawansowany lead generation",
        description: "Zautomatyzuj pozyskiwanie i kwalifikację leadów z LinkedIn.",
        steps: [
          "Integracja LinkedIn z CRM (HubSpot, Salesforce)",
          "Wdróż lead scoring oparty na LinkedIn engagement",
          "Wykorzystaj LinkedIn Forms do bezpośredniego pozyskiwania kontaktów",
          "Testuj LinkedIn Message Ads dla high-intent leads",
          "Stwórz dedykowane landing pages z LinkedIn Insight Tag"
        ]
      },
      {
        title: "Thought leadership na skalę",
        description: "Zbuduj pozycję eksperta poprzez multimedia content i kolaboracje.",
        steps: [
          "Regularny LinkedIn Newsletter (minimum 2x miesięcznie)",
          "LinkedIn Live lub Audio Events co 2 tygodnie",
          "Guest speaking na branżowych eventach (promowanych na LinkedIn)",
          "Kolaboracje z innymi thought leaders (collaborative articles, takeovers)",
          "Publikuj long-form articles na LinkedIn Pulse"
        ]
      }
    ],
    competitiveAdvantages: [
      {
        title: "Własny LinkedIn Mastermind",
        description: "Stwórz ekskluzywną grupę 10-15 top liderów z branży. Regularne spotkania (miesięczne video calls), wymiana doświadczeń, wzajemne promowanie. Taka grupa może generować 30-50% więcej high-quality leads."
      },
      {
        title: "Data-driven content creation",
        description: "Wykorzystuj narzędzia analityczne (Shield Analytics, Taplio) do identyfikacji trendów zanim staną się mainstreamem. First mover advantage na LinkedIn może dać 10x zasięg."
      },
      {
        title: "LinkedIn as a media company",
        description: "Traktuj LinkedIn jak media company: produkuj high-quality multimedia content (video interviews, podcasts promoted on LI), repurpose na różne formaty, buduj content ecosystem."
      },
      {
        title: "Strategic partnerships",
        description: "Partnerstwa z komplementarnymi brandami dla co-marketingu na LinkedIn. Wzajemne guest posts, shared webinary, bundle offers. Pozwala dotrzeć do nowych odbiorców przy minimalnym koszcie."
      }
    ],
    quickWins: [
      {
        title: "LinkedIn Ads pilot",
        description: "Wystartuj z kampanią Sponsored Content (3000 zł budżet testowy)",
        timeline: "Ten tydzień",
        impact: "Wysoki - natychmiastowy wzrost zasięgu"
      },
      {
        title: "Employee advocacy kickoff",
        description: "Zorganizuj warsztaty LinkedIn dla 5 kluczowych pracowników",
        timeline: "2 tygodnie",
        impact: "Bardzo wysoki - 5x zasięg organiczny"
      },
      {
        title: "LinkedIn Newsletter launch",
        description: "Uruchom newsletter i zapromuj na profilu",
        timeline: "Ten tydzień",
        impact: "Wysoki - buduje lojalną audiencję"
      },
      {
        title: "Mastermind group",
        description: "Zaproś 10 liderów z branży do ekskluzywnej grupy",
        timeline: "2 tygodnie",
        impact: "Bardzo wysoki - long-term strategic asset"
      }
    ]
  };
}
